import * as React from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useUpdateAllocation } from "@/hooks/useContainers";
import { ApiError } from "@/lib/api";
import type { ContainerAllocationDto } from "@/types/api";

const FIELDS = [
  { key: "mailboxes_allocated",  label: "Mailboxes",     type: "int"     },
  { key: "bulk_emails_allocated", label: "Bulk emails",   type: "int"    },
  { key: "aliases_allocated",     label: "Aliases",       type: "int"    },
  { key: "forwarders_allocated",  label: "Forwarders",    type: "int"    },
  { key: "storage_gb_allocated",  label: "Storage (GB)",  type: "decimal" },
  { key: "websites_allocated",    label: "Websites",      type: "int"    },
  { key: "smtp_relays_allocated", label: "SMTP relays",   type: "int"    },
  { key: "ssl_certs_allocated",   label: "SSL certs",     type: "int"    }
] as const;

type FieldKey = (typeof FIELDS)[number]["key"];

interface AllocationEditorProps {
  containerId: number;
  initial: ContainerAllocationDto;
}

/**
 * Inline editor for per-domain quotas. Backend rejects the PATCH if the
 * numbers would exceed pool OR fall below current usage — both errors
 * arrive with a structured `reasons` map that we surface inline.
 */
export function AllocationEditor({ containerId, initial }: AllocationEditorProps) {
  const [values, setValues] = React.useState<ContainerAllocationDto>(initial);
  const [mode, setMode] = React.useState(initial.allocation_mode || "Manual");
  const [perFieldErrors, setPerFieldErrors] = React.useState<Record<string, string>>({});
  const { toast } = useToast();
  const update = useUpdateAllocation(containerId);

  // Re-sync when the container is refetched after a successful PATCH.
  React.useEffect(() => { setValues(initial); setMode(initial.allocation_mode); }, [initial]);

  function set(key: FieldKey, raw: string) {
    const n = raw === "" ? 0 : Number(raw);
    setValues(prev => ({ ...prev, [key]: Number.isNaN(n) ? 0 : n }));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setPerFieldErrors({});
    try {
      await update.mutateAsync({ ...values, allocation_mode: mode });
      toast({ title: "Allocation saved", variant: "success" });
    } catch (err) {
      if (err instanceof ApiError && err.body && typeof err.body === "object") {
        const body = err.body as { reasons?: Record<string, string>; message?: string; code?: string };
        if (body.reasons) {
          setPerFieldErrors(
            Object.fromEntries(Object.entries(body.reasons).map(([k, v]) => [`${k}_allocated`, v]))
          );
        }
        toast({
          title: body.code === "capacity_exceeded"
            ? "Allocation exceeds your pool"
            : body.code === "below_usage_floor"
              ? "Can't drop below current usage"
              : "Couldn't save allocation",
          description: body.message ?? err.message,
          variant: "destructive"
        });
      } else {
        toast({ title: "Couldn't save", description: "Network error.", variant: "destructive" });
      }
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Allocation</CardTitle>
        <CardDescription>
          How much of your pool is reserved for this domain. Must fit within the pool and
          can't drop below what's currently in use.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            {FIELDS.map(field => (
              <div key={field.key} className="space-y-1.5">
                <Label htmlFor={field.key}>{field.label}</Label>
                <Input
                  id={field.key}
                  type="number"
                  inputMode={field.type === "decimal" ? "decimal" : "numeric"}
                  min={0}
                  step={field.type === "decimal" ? "0.01" : "1"}
                  value={String(values[field.key] ?? 0)}
                  onChange={e => set(field.key, e.target.value)}
                  aria-invalid={!!perFieldErrors[field.key]}
                />
                {perFieldErrors[field.key] && (
                  <p className="text-xs text-destructive">{perFieldErrors[field.key]}</p>
                )}
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-1">
              <Label htmlFor="mode">Mode</Label>
              <select
                id="mode"
                value={mode}
                onChange={e => setMode(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:w-48"
              >
                <option value="Auto">Auto (share pool with siblings)</option>
                <option value="Manual">Manual (reserved slot)</option>
              </select>
            </div>

            <Button type="submit" disabled={update.isPending} className="sm:self-end">
              {update.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Save allocation
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
